'use client';

import { useMemo, type JSX } from 'react';
import { combine } from '@core/utils/stringUtils';
import {
  Flex,
  Show,
  Stack,
  Button,
  HStack,
  Fieldset,
  type StackProps,
} from '@chakra-ui/react';
import {
  useFieldArray,
  useFormContext,
  type FieldPath,
  type FieldValues,
  type FieldArrayPath,
  type UseFieldArrayProps,
  type UseFieldArrayAppend,
  type UseFieldArrayReturn,
} from 'react-hook-form';

import { TrashIcon } from '../Icon';
import { FormInput } from './FormInput';

interface IFormArrayInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> {
  name: TName;
  label?: string;
  supportiveText?: string;
  shouldUnregister?: boolean;
  containerProps?: StackProps;
  rules?: UseFieldArrayProps<TFieldValues, TName>['rules'];
  control?: UseFieldArrayProps<TFieldValues, TName>['control'];
  render?: (params: UseFieldArrayReturn<TFieldValues, TName>) => JSX.Element;
  addMoreButton?: {
    text: string;
    onClick: (params: {
      add: UseFieldArrayAppend<TFieldValues, TName>;
    }) => () => void;
  };
}

export function FormArrayInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
>({
  name,
  label,
  rules,
  render,
  control,
  addMoreButton,
  supportiveText,
  containerProps,
}: IFormArrayInputProps<TFieldValues, TName>): JSX.Element {
  const { text, onClick } = addMoreButton || {};
  const { getFieldState } = useFormContext();
  const { error } = getFieldState(name);

  const methods = useFieldArray({
    name,
    rules,
    control,
  });

  const content = useMemo(() => {
    const { fields, remove, ...rest } = methods;

    if (render) {
      return render({
        fields,
        remove,
        ...rest,
      });
    }

    return (
      <HStack>
        {fields.map((field, index) => {
          return (
            <FormInput
              key={field.id}
              control={control}
              name={
                combine(
                  { joinWith: '.' },
                  name,
                  index.toString(),
                ) as FieldPath<TFieldValues>
              }
              containerProps={{
                endElement: <TrashIcon />,
                endElementProps: {
                  fill: 'red.500',
                  cursor: 'pointer',
                  onClick: () => {
                    remove(index);
                  },
                },
              }}
            />
          );
        })}
      </HStack>
    );
  }, [control, methods, name, render]);

  return (
    <Fieldset.Root invalid={!!error}>
      <Stack h='100%' gap={6} {...containerProps}>
        <Stack>
          <Fieldset.Legend>{label}</Fieldset.Legend>
          <Fieldset.HelperText>{supportiveText}</Fieldset.HelperText>
        </Stack>
        <Fieldset.ErrorText>{error?.message}</Fieldset.ErrorText>
        <Fieldset.Content>{content}</Fieldset.Content>
        <Show when={!!addMoreButton}>
          <Flex flex={1} alignItems='flex-end'>
            <Button
              flex={1}
              onClick={onClick?.({
                add: methods.append,
              })}
            >
              {text}
            </Button>
          </Flex>
        </Show>
      </Stack>
    </Fieldset.Root>
  );
}
